import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { UserService } from './user.service';

/**
 * Owns the access-token refresh call and guarantees only ONE refresh runs at a
 * time. When many requests 401 at once (their access token expired together),
 * they all share the same in-flight refresh via shareReplay and then retry.
 *
 * Cross-tab safety lives on the SERVER (AuthController.Refresh): a token revoked as
 * 'Rotated' within the grace window is accepted instead of being treated as reuse, and
 * a genuine reuse revokes only that login family. That covers two tabs refreshing at
 * once without this client needing to coordinate them.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {

  private apiUrl = environment.apiUrl;
  private inFlight: Observable<string> | null = null;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private router: Router
  ) {}

  /** Returns the new access token. Concurrent callers share one refresh request. */
  refreshToken(): Observable<string> {
    if (this.inFlight) {
      return this.inFlight;
    }

    const refreshToken = this.userService.getRefreshToken();
    const accessToken = this.userService.getAccessToken();

    // No refresh token → nothing we can do; caller will force logout.
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const url = `${this.apiUrl}/api/Auth/Refresh`;
    this.inFlight = this.http
      .post<{ token: string; refreshToken: string }>(url, { accessToken, refreshToken })
      .pipe(
        tap((res) => {
          if (res?.token) { this.userService.setAccessToken(res.token); }
          if (res?.refreshToken) { this.userService.setRefreshToken(res.refreshToken); }
        }),
        map((res) => res?.token),
        shareReplay(1),
        finalize(() => { this.inFlight = null; })
      );

    return this.inFlight;
  }

  /**
   * Move the session to another customer and adopt the re-issued access token.
   *
   * Changing the customer used to touch sessionStorage only, so the token's customerId went
   * stale and the API could not pin a request's CID to the claim — the check had to be
   * disabled, leaving any authenticated caller able to pass any CID (IDOR). The API now
   * authorises the move against the caller's own token and returns a token carrying the new
   * customer as activeCustomerId, so the CID check is enforced again.
   *
   * Callers must wait for this before firing customer-scoped requests, otherwise those
   * requests race the new token. The refresh token is unchanged — same session, new scope.
   */
  switchCustomer(customerId: number | string): Observable<void> {
    const url = `${this.apiUrl}/api/Auth/SwitchCustomer?customerId=${encodeURIComponent(String(customerId))}`;
    return this.http.post<{ token: string }>(url, {}).pipe(
      tap((res) => {
        if (res?.token) { this.userService.setAccessToken(res.token); }
        this.userService.setCustomerId(Number(customerId));
      }),
      map(() => void 0)
    );
  }

  /**
   * Return from an impersonated client-portal session to the ORIGINAL one (the
   * "back to Entity Dashboard" path), then reload so the whole app re-initialises
   * under it.
   *
   * The backup holds the access token as it was when impersonation STARTED, and access
   * tokens live only 5 minutes (JwtSettings.ExpirationInMinutes) — nothing renews the
   * stored copy while the user is in the client portal, so by the time they come back it
   * is almost always expired. Reloading on it made the restored session depend on a 401
   * being caught and refreshed after boot; if anything mishandled that 401 the user was
   * logged out and, because forceLogout clears sessionStorage, the backup went with it —
   * making the original session unrecoverable.
   *
   * So mint a fresh access token from the backed-up REFRESH token (valid for days) BEFORE
   * reloading. On failure we still reload: the interceptor's own 401 handling is then the
   * fallback, exactly as before.
   */
  restoreBackupSessionAndReload(targetUrl?: string): void {
    this.userService.restoreBackupSession();

    // Consume the backup. Leaving it in place would let a LATER, genuine authorization
    // failure re-enter this path with a session that is already restored — the guard
    // would restore, navigate, be denied and restore again, forever.
    sessionStorage.removeItem('userDataBackup');

    // A full document load (not router navigation) so the shell — sidebar, tabs, menus,
    // all built for the client-portal role — is rebuilt under the restored one.
    const leave = () => {
      if (targetUrl) { window.location.href = targetUrl; } else { window.location.reload(); }
    };
    this.refreshToken().subscribe({ next: leave, error: leave });
  }

  /**
   * Refresh failed / no session left — clear everything and go to login.
   *
   * The impersonation backup is deliberately carried across the wipe: an impersonated
   * client-portal session dying must not destroy the ORIGINAL session's credentials, or
   * the user can never get back to the Entity Dashboard. It is a snapshot of a session
   * that is still valid on the server, and the Login page clears sessionStorage on load,
   * so it cannot outlive a genuine sign-out.
   */
  forceLogout(): void {
    const backup = sessionStorage.getItem('userDataBackup');
    sessionStorage.clear();
    if (backup) { sessionStorage.setItem('userDataBackup', backup); }
    if (environment.isLogoOutFromLive === '1') {
      window.location.href = 'https://login.teamleaseregtech.com/logout.aspx';
    } else {
      this.router.navigate(['/Login']);
    }
  }
}
